import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faTags } from "@fortawesome/free-solid-svg-icons";
import { Breadcrumb, Button, Card, Col, Form, Row, Table } from "@themesberg/react-bootstrap";

import { Routes } from "../routes";
import { apiRequest, getAuthToken } from "../utils/api";
import { enqueueSync } from "../utils/syncQueue";

const CATEGORIES_STORAGE_KEY = "treasurer.expenseCategories";

const loadCachedCategories = () => {
  const stored = localStorage.getItem(CATEGORIES_STORAGE_KEY);
  if (!stored) return [];
  try {
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

export default () => {
  const [categories, setCategories] = useState(loadCachedCategories);
  const [categoryForm, setCategoryForm] = useState(() => ({ name: "", description: "" }));
  const [editingId, setEditingId] = useState(null);
  const [editingValues, setEditingValues] = useState(() => ({ name: "", description: "" }));
  const [error, setError] = useState("");

  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;
    apiRequest("/expense-categories")
      .then((data) => {
        if (data?.categories) {
          setCategories(data.categories.map((c) => ({ id: String(c.id), name: c.name, description: c.description || "" })));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem(CATEGORIES_STORAGE_KEY, JSON.stringify(categories));
  }, [categories]);

  const handleAddCategory = (e) => {
    e.preventDefault();
    const name = (categoryForm?.name ?? "").trim();
    if (!name) return;
    setError("");
    const tempId = `cat-${Date.now()}`;
    const newCat = { id: tempId, name, description: (categoryForm?.description ?? "").trim() || "" };
    setCategories((prev) => [...prev, newCat]);
    enqueueSync({
      type: "expense-category.create",
      payload: { localId: tempId, name, description: newCat.description || undefined }
    });
    setCategoryForm({ name: "", description: "" });
    const token = getAuthToken();
    if (token && navigator.onLine) {
      apiRequest("/expense-categories", {
        method: "POST",
        body: JSON.stringify({ name, description: newCat.description || undefined })
      })
        .then((data) => {
          if (data?.category?.id) {
            setCategories((prev) =>
              prev.map((c) => (c.id === tempId ? { ...c, id: String(data.category.id) } : c))
            );
          }
        })
        .catch((err) => {
          setCategories((prev) => prev.filter((c) => c.id !== tempId));
          setError(err?.message || "Failed to add category");
        });
    }
  };

  const handleStartEdit = (cat) => {
    setEditingId(cat.id);
    setEditingValues({ name: cat.name || "", description: cat.description || "" });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingValues({ name: "", description: "" });
  };

  const handleSaveEdit = (cat) => {
    const name = (editingValues?.name ?? "").trim();
    if (!name) return;
    setError("");
    const desc = (editingValues?.description ?? "").trim();
    setCategories((prev) =>
      prev.map((c) => (c.id === cat.id ? { ...c, name, description: desc || "" } : c))
    );
    setEditingId(null);
    setEditingValues({ name: "", description: "" });
    if (!String(cat.id).startsWith("cat-") && navigator.onLine) {
      apiRequest(`/expense-categories/${cat.id}`, {
        method: "PUT",
        body: JSON.stringify({ name, description: desc || undefined })
      }).catch((err) => setError(err?.message || "Failed to update"));
    }
  };

  const handleDelete = async (cat) => {
    setError("");
    setCategories((prev) => prev.filter((c) => c.id !== cat.id));
    if (!String(cat.id).startsWith("cat-") && navigator.onLine) {
      try {
        await apiRequest(`/expense-categories/${cat.id}`, { method: "DELETE" });
      } catch (err) {
        setCategories((prev) => [...prev, cat]);
        setError(err?.message || "Cannot delete category (may be in use by expenses)");
      }
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <Breadcrumb className="d-none d-md-inline-block" listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}>
            <Breadcrumb.Item>
              <FontAwesomeIcon icon={faHome} />
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Expenses</Breadcrumb.Item>
          </Breadcrumb>
          <h4>Expenses</h4>
          <p className="mb-0">Manage expense types used when recording expenses in any group.</p>
        </div>
      </div>

      <Row>
        <Col xs={12} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Add expense type</h5>
            </Card.Header>
            <Card.Body>
              {error ? <div className="text-danger small mb-2">{error}</div> : null}
              <Form onSubmit={handleAddCategory}>
                <Row className="g-2">
                  <Col md={4}>
                    <Form.Group className="mb-0">
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. Tent, Food, Transport"
                        value={categoryForm?.name ?? ""}
                        onChange={(e) => {
                        const value = e.target != null ? e.target.value : "";
                        setCategoryForm((prev) => ({ ...prev, name: value }));
                      }}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-0">
                      <Form.Label>Description (optional)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Optional description"
                        value={categoryForm?.description ?? ""}
                        onChange={(e) => {
                        const value = e.target != null ? e.target.value : "";
                        setCategoryForm((prev) => ({ ...prev, description: value }));
                      }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="d-flex align-items-end pb-2 pb-md-0">
                    <Button type="submit" variant="primary" disabled={!(categoryForm?.name ?? "").trim()}>
                      Add
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>

          <Card border="light" className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Expense types</h5>
              <span className="text-muted">{categories.length} item{categories.length !== 1 ? "s" : ""}</span>
            </Card.Header>
            <Card.Body className="pt-0">
              {categories.length === 0 ? (
                <p className="text-muted mb-0">No expense types yet. Add one above; they can be used when recording expenses in any group.</p>
              ) : (
                <Table responsive className="table-centered mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th className="border-0">Name</th>
                      <th className="border-0">Description</th>
                      <th className="border-0 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((cat) => {
                      const isEditing = editingId === cat.id;
                      return (
                        <tr key={cat.id}>
                          <td className="fw-bold">
                            {isEditing ? (
                              <Form.Control
                                size="sm"
                                value={editingValues?.name ?? ""}
                                onChange={(e) => {
                                const value = e.target != null ? e.target.value : "";
                                setEditingValues((prev) => ({ ...prev, name: value }));
                              }}
                              />
                            ) : (
                              cat.name
                            )}
                          </td>
                          <td>
                            {isEditing ? (
                              <Form.Control
                                size="sm"
                                value={editingValues?.description ?? ""}
                                onChange={(e) => {
                                const value = e.target != null ? e.target.value : "";
                                setEditingValues((prev) => ({ ...prev, description: value }));
                              }}
                              />
                            ) : (
                              cat.description || "–"
                            )}
                          </td>
                          <td className="text-end">
                            {isEditing ? (
                              <>
                                <Button size="sm" variant="outline-primary" className="me-2" onClick={() => handleSaveEdit(cat)} disabled={!(editingValues?.name ?? "").trim()}>
                                  Save
                                </Button>
                                <Button size="sm" variant="outline-secondary" onClick={handleCancelEdit}>
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => handleStartEdit(cat)}>
                                  Edit
                                </Button>
                                <Button size="sm" variant="outline-danger" onClick={() => handleDelete(cat)}>
                                  Delete
                                </Button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};
